import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Button, Checkbox, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE, TEXT } from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetScope, AssetType, PERMISSION_TYPE } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { ArticleIcon as FileIcon } from '@/resources';
import { useCreateAssetMutation, useGetPublicAssets } from '@/services';
import { CreateEditAssetPayload, RecommendedSpeechesProps } from '@/types';

export const RecommendedSpeeches = ({
  handleOpenCreateMode,
  eventId,
  isDisabledAddButton,
  numberOfAssets,
  slotsNumber,
  refetchTextAssets
}: RecommendedSpeechesProps) => {
  const [t] = useTranslation('common');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);
  const { inView } = useInView();
  const { permissions } = useContext(AuthContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const { data, fetchNextPage, hasNextPage, refetch } = useGetPublicAssets(
    [''],
    PAGE_SIZE,
    undefined,
    '',
    [AssetType.TEXT.toString()]
  );

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  const handleChange = (id: string) => {
    const numId = parseInt(id, 10);
    if (selectedIds.includes(numId)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== numId));
    } else {
      setSelectedIds([...selectedIds, numId]);
    }
  };

  const { mutate: createTextAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: (response) => {
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const handleSubmit = async () => {
    const speechesToAdd = data?.pages
      .flatMap((page) => page.data?.items)
      .filter((item) => selectedIds.includes(Number(item.id)));

    if (speechesToAdd) {
      for (const speech of speechesToAdd) {
        const textAsset: CreateEditAssetPayload = {
          eventId: Number(eventId),
          assetType: AssetType.TEXT,
          assetScope: AssetScope.PRIVATE,
          assetName: speech.assetName,
          assetUrl: '',
          assetSize: 0,
          thumbnailUrl: '',
          additionalData: speech.additionalData,
          ...(!hasOrgAccess && {
            tags: [
              {
                name: TEXT
              }
            ]
          })
        };
        setSelectedIds([]);

        await new Promise<void>((resolve, reject) => {
          createTextAsset(textAsset, {
            onSuccess: () => {
              resolve();
              refetchTextAssets();
            },
            onError: (error) => {
              reject(error);
            }
          });
        });
      }
    }
  };

  return (
    <div className='flex w-full flex-col rounded-lg pb-4 sm:pb-6 md:w-1/2'>
      <div className='mb-2 flex w-full justify-between border-b border-secondary-blue-gray pb-2 lg:mb-6'>
        <Label text={t('recommended')} type={LABEL_TYPE.H2} />
      </div>
      <div className='mb-5 w-full'>
        {data?.pages
          .flatMap((page) => page.data?.items)
          .map((item) => (
            <div
              key={item.id}
              className={`${
                isDisabledAddButton && 'pointer-events-none'
              } flex max-h-10 max-w-full cursor-pointer items-center justify-between gap-2.5 rounded-lg py-1.5 lg:gap-4`}
              onClick={() => handleOpenCreateMode(item)}>
              <div className='flex flex-row gap-x-2'>
                <FileIcon className='text-2xl lg:text-xl' />
                <label className='w-fit truncate text-sm'>{item.assetName}</label>
              </div>
              <Checkbox
                id='chooseText'
                isChecked={selectedIds.includes(item.id)}
                onChange={() => handleChange(item.id)}
                classes='w-3 h-3'
              />
            </div>
          ))}
      </div>
      <div className='flex w-full justify-end !text-sm !font-normal !text-primary-blue'>
        <Button
          disabled={
            (!hasOrgAccess &&
              (isDisabledAddButton || selectedIds.length + numberOfAssets > slotsNumber)) ||
            selectedIds.length === 0
          }
          type='submit'
          variant={ButtonVariant.SECONDARY}
          handleOnClick={handleSubmit}
          className={`${
            (!hasOrgAccess &&
              (isDisabledAddButton || selectedIds.length + numberOfAssets > slotsNumber)) ||
            selectedIds.length === 0
              ? 'text-secondary-steel-blue'
              : 'text-primary-blue'
          }`}>
          {selectedIds.length === 0 ? t('chooseText') : t('addSelectedText')}
        </Button>
      </div>
    </div>
  );
};
